import React, { useState, useEffect } from 'react';
import portalTaskService from '../../services/portalTaskService';
import { CheckSquare, Circle, CheckCircle, AlertCircle } from 'lucide-react';

function PortalTasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const response = await portalTaskService.getTasks();
        setTasks(response.data.data || []);
      } catch (err) {
        console.error('Failed to load tasks:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const statusIcon = (s) => {
    switch (s) {
      case 'completed': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'in_progress': return <Circle className="h-4 w-4 text-blue-500" />;
      default: return <Circle className="h-4 w-4 text-gray-400" />;
    }
  };

  const priorityColors = {
    urgent: 'text-red-600',
    high: 'text-orange-600',
    medium: 'text-yellow-600',
    low: 'text-gray-500',
  };

  const isOverdue = (dueDate, s) => {
    if (!dueDate || s === 'completed') return false;
    return new Date(dueDate) < new Date();
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Tasks</h1>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {tasks.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <CheckSquare className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p>No tasks to display.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {tasks.map((task) => {
              const overdue = isOverdue(task.due_date, task.status);
              return (
                <div key={task.id} className="p-4 flex items-start gap-3">
                  <div className="mt-0.5">{statusIcon(task.status)}</div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${task.status === 'completed' ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                      {task.title}
                    </p>
                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                      {task.case_title && <span>{task.case_title}</span>}
                      {task.due_date && (
                        <span className={`flex items-center gap-1 ${overdue ? 'text-red-600 font-medium' : ''}`}>
                          {overdue && <AlertCircle className="h-3 w-3" />}
                          Due {new Date(task.due_date).toLocaleDateString()}
                        </span>
                      )}
                      {task.priority && (
                        <span className={`font-medium ${priorityColors[task.priority] || ''}`}>
                          {task.priority}
                        </span>
                      )}
                    </div>
                    {task.description && (
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">{task.description}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default PortalTasks;
